import {
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { PopoverModule, Popover } from 'primeng/popover';
import {
  AdminNotificationService,
  AdminNotification,
} from '../../services/admin-notification';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { IndDatePipe } from '../../pipes/ind-date';

/** Topbar bell + unread badge + notifications popover. Admin only. */
@Component({
  selector: 'app-admin-bell',
  standalone: true,
  imports: [CommonModule, BadgeModule, ButtonModule, PopoverModule, IndDatePipe],
  templateUrl: './admin-bell.html',
  styleUrls: ['./admin-bell.css'],
})
export class AdminBell implements OnInit, OnDestroy {
  private svc = inject(AdminNotificationService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  @ViewChild('op') op!: Popover;

  readonly unread = this.svc.unread; // shared signal
  items = signal<AdminNotification[]>([]);
  loading = signal(false);

  private pollTimer: any;
  private countSub?: Subscription;
  private greeted = false;

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.auth.isAdmin()) return;

    this.refreshCount(true);
    // Keep the badge fresh.
    this.pollTimer = setInterval(() => this.refreshCount(false), 60_000);
  }

  ngOnDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.countSub?.unsubscribe();
  }

  private refreshCount(greetOnLogin: boolean) {
    // One in-flight count request at a time; also drops any pending one on destroy.
    this.countSub?.unsubscribe();
    this.countSub = this.svc.loadUnreadCount().subscribe({
      next: (n) => {
        if (greetOnLogin && !this.greeted && n > 0) {
          this.greeted = true;
          this.toast.info(`You have ${n} notification${n === 1 ? '' : 's'} to review.`);
        }
      },
    });
  }

  toggle(event: Event) {
    this.op.toggle(event);
    this.loadList();
  }

  /** The bell only lists UNREAD alerts — reading one removes it from here. */
  private loadList() {
    this.loading.set(true);
    this.svc.getAll(true, 50).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.items.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  open(n: AdminNotification) {
    const finish = () => {
      this.op?.hide();
      // Route by alert type: partner-app issues live on the App Notifications
      // screen; everything else with a refId is a KYC submission.
      if (n.type === 'PARTNER_ISSUE') {
        this.router.navigate(['/app-notifications'], {
          queryParams: { tab: 'issues', ...(n.refId != null ? { issueId: n.refId } : {}) },
        });
      } else if (n.refId != null) {
        this.router.navigate(['/partner-kyc-review'], { queryParams: { partnerId: n.refId } });
      }
    };
    if (n.isRead) {
      finish();
      return;
    }
    this.svc.markRead(n.id).subscribe({
      next: () => {
        // Read => drop it from the bell list and decrement the badge.
        this.items.set(this.items().filter((x) => x.id !== n.id));
        this.svc.unread.set(Math.max(0, this.unread() - 1));
        finish();
      },
      error: finish,
    });
  }

  markAllRead() {
    this.svc.markAllRead().subscribe({
      next: () => {
        // All read => clear the bell list and badge.
        this.items.set([]);
        this.svc.unread.set(0);
        this.toast.success('All notifications marked read');
      },
    });
  }

  /** Jump to the full notifications page (read + unread history). */
  seeAll() {
    this.op?.hide();
    this.router.navigate(['/admin-notifications']);
  }
}
